import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, List, MessageCircle, Calendar, Calculator, LayoutTemplate, Search, Users, DollarSign } from 'lucide-react';
export function SolutionSection() {
  const deliverables = [{
    icon: <FileText className="w-6 h-6" data-id="element-168" />,
    title: 'El Blueprint Maestro (PDF)',
    desc: 'La guía paso a paso escrita para padres e hijos.'
  }, {
    icon: <List className="w-6 h-6" data-id="element-169" />,
    title: 'El Menú de los 10 Micro-Servicios',
    desc: 'Ideas validadas en vecindarios de EE.UU. que pagan entre $10 y $50.'
  }, {
    icon: <MessageCircle className="w-6 h-6" data-id="element-170" />,
    title: "Scripts de 'Venta sin Miedo'",
    desc: "Lo que tu hijo debe decir palabra por palabra para que los vecinos digan '¡SÍ!'."
  }, {
    icon: <Calendar className="w-6 h-6" data-id="element-171" />,
    title: 'Checklist de Lanzamiento 48h',
    desc: 'El cronograma exacto de Sábado y Domingo.'
  }, {
    icon: <Calculator className="w-6 h-6" data-id="element-172" />,
    title: 'Calculadora del Mini-CEO',
    desc: 'Para registrar ingresos y repartir ganancias (Ahorro/Gasto/Inversión).'
  }, {
    icon: <LayoutTemplate className="w-6 h-6" data-id="element-173" />,
    title: 'Diseño de Volante Express',
    desc: 'Plantillas listas para imprimir o copiar a mano.'
  }];
  const steps = [{
    icon: <Search className="w-8 h-8" data-id="element-174" />,
    title: 'Identifica',
    desc: "Usan el 'Radar de Oportunidades' para ver qué necesita el vecino."
  }, {
    icon: <Users className="w-8 h-8" data-id="element-175" />,
    title: 'Conecta',
    desc: 'Usan el Script de Oro para ofrecer su ayuda con confianza.'
  }, {
    icon: <DollarSign className="w-8 h-8" data-id="element-176" />,
    title: 'Cobra',
    desc: 'Realizan el servicio y dividen el dinero bajo la regla 50/30/20.'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="py-20 md:py-32 bg-white px-4" data-id="element-177">
      <div className="max-w-5xl mx-auto" data-id="element-178">
        {/* The Unique Mechanism */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-24" data-id="element-179">
          <div className="inline-block bg-navy text-white font-bold px-6 py-2 rounded-full text-sm mb-6 uppercase tracking-wider" data-id="element-180">
            El Método de las 3 "S"
          </div>
          <h2 className="text-3xl md:text-5xl font-heading font-black text-navy mb-8" data-id="element-181">
            Servicio, Simple, Saldo
          </h2>
          <p className="text-lg md:text-xl text-gray-600 font-body max-w-3xl mx-auto leading-relaxed" data-id="element-182">
            A diferencia de otros programas, no necesitamos inversión inicial ni
            aplicaciones complicadas. Usamos la{' '}
            <strong data-id="element-183">"Economía del Vecindario"</strong>: identificar un problema
            real que un adulto cansado tiene hoy y resolverlo en menos de 20
            minutos. Es acción inmediata que genera billetes reales en la mano
            de tu hijo en menos de 48 horas.
          </p>
        </motion.div>

        {/* How It Works */}
        <div className="mb-24" data-id="element-184">
          <h3 className="text-3xl font-heading font-bold text-center text-navy mb-12" data-id="element-185">
            Cómo Funciona
          </h3>
          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-186">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gray-200 z-0" data-id="element-187" />

            {steps.map((step, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-188">
                <div className="w-24 h-24 bg-white rounded-full border-4 border-orange flex items-center justify-center text-orange mb-6 shadow-lg" data-id="element-189">
                  {step.icon}
                </div>
                <h4 className="text-2xl font-heading font-bold text-navy mb-3" data-id="element-190">
                  {step.title}
                </h4>
                <p className="font-body text-gray-600" data-id="element-191">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>

        {/* What You Get */}
        <div className="bg-navy text-white rounded-3xl p-8 md:p-12 shadow-2xl" data-id="element-192">
          <h3 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12" data-id="element-193">
            Todo lo que recibes hoy
          </h3>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-2 gap-6" data-id="element-194">
            {deliverables.map((item, idx) => <motion.div key={idx} variants={itemVariants} className="flex items-start gap-4 p-6 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors" data-id="element-195">
                <div className="flex-shrink-0 mt-1 text-orange" data-id="element-196">
                  {item.icon}
                </div>
                <div data-id="element-197">
                  <h4 className="font-heading font-bold text-lg mb-1" data-id="element-198">
                    {item.title}
                  </h4>
                  <p className="font-body text-gray-300 text-sm" data-id="element-199">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}